import { Machine, interpret, assign } from "xstate";
import { loadRasters } from "../../api/download";
import { flatten, uniq, compose, prop, map } from "ramda";
import { isNotEmpty, filterByKey } from "../../libs/functional-utils";
import { downloadStore } from "../../models/download/download";

export const DownloadEventType = {
  INIT: "INITIALIZE",
  ASSIGN_IDS: "ASSIGN_IDS",
  DOWNLOAD: "DOWNLOAD",
  DOWNLOADED: "DOWNLOAD",
  ADD_URL: "ADD_URL",
  LOAD_DOWNLOAD_URLS: "LOAD_DOWNLOAD_URLS",
  UPDATE_IDS: "UPDATE_IDS",
  UPDATE_FILTERS: "UPDATE_FILTERS",
  UPDATE_COLLECTIONS: "UPDATE_COLLECTIONS",
  CANCEL: "CANCEL",
  RETRY: "RETRY",
};

export const machine = Machine({
  id: "download",
  context: {
    urls: [],
    links: [],
    filters: [
      {
        title: "Visible",
        selected: false,
      },
      {
        title: "Available",
        selected: false,
      },
    ],
    collections: [],
    ids: [],
    view: null,
    imagery: null,
    layers: null,
    event: null,
  },
  initial: "idle",
  on: {
    [DownloadEventType.ASSIGN_IDS]: {
      actions: "assignIds",
    },
  },
  states: {
    idle: {
      on: {
        [DownloadEventType.INIT]: {
          target: "initialized",
          actions: ["assignInitialData"],
        },
      },
    },
    initialized: {
      initial: "idle",
      on: {
        [DownloadEventType.LOAD_DOWNLOAD_URLS]: {
          target: ".loading_urls",
          actions: ["assignEvent"],
        },
      },
      states: {
        idle: {},
        loading_urls: {
          invoke: {
            src: "loadDownloadUrls",
            onDone: {
              target: "loaded_urls",
              actions: ["assignUrls", "setFilters"],
            },
            onError: {
              target: "error",
              actions: ["assignError"],
            },
          },
        },
        loaded_urls: {
          on: {
            [DownloadEventType.DOWNLOAD]: {
              target: "downloading",
              actions: ["assignLinks", "downloadImages"],
            },
            [DownloadEventType.CANCEL]: "canceled",
          },
        },
        downloading: {
          on: {
            [DownloadEventType.DOWNLOADED]: "downloaded",
          },
        },
        downloaded: {},
        canceled: {},
        error: {},
      },
    },
  },
});

const config = {
  guards: {},
  services: {
    loadDownloadUrls: (context, event) =>
      loadRasters({
        event: context.event,
        view: context.view,
        ids: context.ids,
        layers: context.layers,
        imagery: context.imagery,
      }),
  },
  actions: {
    downloadImages: (context, event) => {
      const downloadUrls = event.payload.links;
      const urls = [...downloadUrls];
      if (urls.length > 0) {
        let url = urls[0].split("?")[0];
        const uuids = [...urls]
          .map((url) => url.split("=")[1])
          .filter((uuid) => uuid !== undefined);
        const zip_url =
          url +
          uuids.reduce((prev, current) => prev.concat(`?uuid=${current}`), "");
        window.open(zip_url, "_blank");
      }
    },
    assignError: assign({
      error: (_, event) => event.data,
    }),
    assignLinks: assign({
      links: (context, event) => event.payload.links,
    }),
    updateFilters: assign({
      filters: (context, event) => event.payload.filters,
    }),
    assignIds: assign({
      ids: (context, event) => {
        return event.payload.visible_ids;
      },
    }),
    assignCollections: assign({
      collections: (_, event) => event.payload.collections,
    }),
    assignEvent: assign({
      event: (_, event) => event.payload.event,
    }),
    assignInitialData: assign({
      view: (_, event) => event.payload.view,
      layers: (_, event) => event.payload.layers,
      imagery: (_, event) => event.payload.imagery,
    }),
    assignUrls: assign({
      urls: (_, event) => {
        downloadStore.setUrls(flatten(event.data));
        return event.data;
      },
    }),
    setFilters: assign({
      filters: (context, event) => {
        const urls = event.data.filter((u) => isNotEmpty(u));
        const types = uniq(flatten(urls).map((u) => u.type));
        const filters = context.filters.map((f) => {
          if (urls.length == 2)
            return { ...f, selected: f.title === "Visible" };
          else {
            if (types[0] === "Available")
              return { ...f, selected: f.title === "Available" };
            return { ...f, selected: f.title === "Visible" };
          }
        });
        downloadStore.setFilters(filters);
        return filters;
      },
    }),
  },
};

export const service = interpret(machine.withConfig(config)).start();

